import { useMemo, useRef, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';

import Button from '@/V2/components/ui/button/button';
import {
  FormField,
  Input,
  MultiValueTextArea,
  Select,
} from '@/V2/components/ui/form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { useAddCustomerPersona } from '../../hooks/use-add-customer-persona';
import { useDeleteCustomerPersona } from '../../hooks/use-delete-customer-persona';
import { useFieldOptions } from '../../hooks/use-field-options';
import {
  useGetCustomerPersonas,
  useUpdateCustomerPersona,
} from '../../hooks/use-update-customer-persona';
import { CustomerPersona as CustomerPersonaType } from '../../types';
import { FormGroup } from '../ui/form-group';

// Predefined options for dropdowns
const customerPersonaSchema = z.object({
  id: z.string().uuid().optional(),
  search_title: z.string().min(1, 'Persona name is required'),
  job_title: z.string(),
  age_range: z.string(),
  income_range: z.string(),
  country: z.string(),
  buying_behavior: z.string(),
  industry: z.string(),
  company_size: z.string(),
  education_level: z.string(),
  customer_pain_points: z.array(z.string()).optional(),
  customer_pain_point_solutions: z.array(z.string()).optional(),
  customer_vocabulary: z.array(z.string()).optional(),
});

const customerPersonasFormSchema = z.object({
  customer_personas: z.array(customerPersonaSchema),
});

type CustomerPersonasFormValues = z.infer<typeof customerPersonasFormSchema>;

interface CustomerPersonaFormProps {
  projectId: string;
}

export const CustomerPersonaForm = ({
  projectId,
}: CustomerPersonaFormProps) => {
  const customerPersonasQuery = useGetCustomerPersonas(projectId);
  const { data: { customer_personas: customerPersonas = [] } = {} } =
    customerPersonasQuery?.data || {};
  const isLoading = customerPersonasQuery?.isLoading || false;
  const { data: fieldOptions } = useFieldOptions(projectId);
  const deleteCustomerPersona = useDeleteCustomerPersona(projectId);
  const addCustomerPersona = useAddCustomerPersona(projectId);
  const updateCustomerPersona = useUpdateCustomerPersona(projectId);

  // Track which persona forms should be expanded
  const [expandedPersonas, setExpandedPersonas] = useState<Set<string>>(
    new Set()
  );

  // Create stable form data that only changes when customer personas data actually changes
  const stableFormData = useMemo(() => {
    if (customerPersonas?.length > 0) {
      return {
        customer_personas: customerPersonas.map(
          (persona: CustomerPersonaType) => ({
            id: persona.id,
            search_title: persona.search_title || '',
            job_title: persona.job_title || '',
            age_range: persona.age_range || '',
            income_range: persona.income_range || '',
            education_level: persona.education_level || '',
            country: persona.country || '',
            buying_behavior: persona.buying_behavior || '',
            industry: persona.industry || '',
            company_size: persona.company_size || '',
            customer_pain_points: persona.customer_pain_points || [],
            customer_pain_point_solutions:
              persona.customer_pain_point_solutions || [],
            customer_vocabulary: persona.customer_vocabulary || [],
          })
        ),
      };
    }

    // Default form data
    return {
      customer_personas: [
        {
          id: undefined,
          search_title: '',
          job_title: '',
          age_range: '',
          income_range: '',
          education_level: '',
          country: '',
          buying_behavior: '',
          industry: '',
          company_size: '',
          customer_pain_points: [],
          customer_pain_point_solutions: [],
          customer_vocabulary: [],
        },
      ],
    };
  }, [customerPersonas]);

  const form = useForm<CustomerPersonasFormValues>({
    resolver: zodResolver(customerPersonasFormSchema),
    defaultValues: stableFormData,
  });

  // Track previous data to handle form synchronization
  const prevDataRef = useRef(stableFormData);

  // Handle form resets when data changes
  if (JSON.stringify(prevDataRef.current) !== JSON.stringify(stableFormData)) {
    form.reset(stableFormData);
    prevDataRef.current = stableFormData;

    // Expand the first persona by default when data changes
    if (customerPersonas?.length > 0) {
      setExpandedPersonas(
        new Set(customerPersonas.map((p: CustomerPersonaType) => p.id))
      );
    } else {
      setExpandedPersonas(new Set(['new_0']));
    }
  }

  const { fields, append, remove } = useFieldArray({
    name: 'customer_personas',
    control: form.control,
  });

  const handleSubmit = form.handleSubmit(
    async (data: CustomerPersonasFormValues) => {
      try {
        const updatePromises = data.customer_personas.map((persona, index) => {
          // Skip if persona has no meaningful data
          if (!persona.search_title && !persona.job_title) {
            return null;
          }

          // Clean the data by removing empty values
          const cleanedPersona = Object.fromEntries(
            Object.entries(persona).filter(([, value]) => {
              if (Array.isArray(value)) {
                return value.length > 0;
              }
              return value !== '' && value !== null && value !== undefined;
            })
          );

          if (!persona.id) {
            // Create new persona
            const newPersona = {
              search_title: persona.search_title || '',
              job_title: persona.job_title || '',
              age_range: persona.age_range || '',
              income_range: persona.income_range || '',
              country: persona.country || '',
              buying_behavior: persona.buying_behavior || '',
              industry: persona.industry || '',
              company_size: persona.company_size || '',
              education_level: persona.education_level || '',
              customer_pain_points: persona.customer_pain_points || [],
              customer_pain_point_solutions:
                persona.customer_pain_point_solutions || [],
              customer_vocabulary: persona.customer_vocabulary || [],
            };

            // Filter out empty fields from the new persona
            const cleanedNewPersona = Object.fromEntries(
              Object.entries(newPersona).filter(([, value]) => {
                if (Array.isArray(value)) {
                  return value.length > 0;
                }
                return value !== '';
              })
            ) as Omit<CustomerPersonaType, 'id'>;

            return addCustomerPersona
              .mutateAsync(cleanedNewPersona)
              .then((result) => {
                // Update expanded personas when new persona gets an ID
                setExpandedPersonas((prev) => {
                  const newSet = new Set(prev);
                  newSet.delete(`new_${index}`); // Remove the temporary key
                  if (result?.id) {
                    newSet.add(result.id); // Add the real ID
                  }
                  return newSet;
                });
                return result;
              });
          } else {
            // Update existing persona
            return updateCustomerPersona.mutateAsync({
              id: persona.id,
              ...cleanedPersona,
            } as CustomerPersonaType);
          }
        });

        await Promise.all(updatePromises.filter(Boolean));
        toast.success('All changes saved successfully');
      } catch (error) {
        console.error('Error saving changes:', error);
        toast.error('Failed to save some changes');
      }
    }
  );

  const handleAddPersona = () => {
    const newIndex = fields.length;
    append({
      id: undefined,
      search_title: '',
      job_title: '',
      age_range: '',
      income_range: '',
      education_level: '',
      country: '',
      buying_behavior: '',
      industry: '',
      company_size: '',
      customer_pain_points: [],
      customer_pain_point_solutions: [],
      customer_vocabulary: [],
    });

    // Expand the newly added persona form
    setExpandedPersonas((prev) => new Set([...prev, `new_${newIndex}`]));
  };

  const handlePersonaDelete = async (index: number) => {
    const persona = form.getValues().customer_personas[index];

    if (!persona.id) {
      // If no id, it's a new entry that hasn't been saved yet
      remove(index);
      setExpandedPersonas((prev) => {
        const newSet = new Set(prev);
        newSet.delete(`new_${index}`);
        return newSet;
      });
      return;
    }

    if (
      window.confirm('Are you sure you want to delete this customer persona?')
    ) {
      try {
        await deleteCustomerPersona.mutateAsync(persona.id);
        remove(index);
        setExpandedPersonas((prev) => {
          const newSet = new Set(prev);
          newSet.delete(persona.id!);
          return newSet;
        });
      } catch (error) {
        console.error('Error deleting customer persona:', error);
        toast.error('Failed to delete customer persona');
      }
    }
  };

  return (
    <div>
      <div className='flex justify-between items-center mb-6'>
        <div className='flex flex-col'>
          <h2 className='text-xl font-semibold mb-1'>Customer Persona</h2>
          <p className='text-sm'>
            Describe your company's key personas (max 2)
          </p>
        </div>
        <div className='flex mt-2 mr-4'>
          <Button
            type='submit'
            variant='primary'
            size='xsmall'
            onClick={handleSubmit}
            isLoading={
              deleteCustomerPersona.isPending ||
              addCustomerPersona.isPending ||
              updateCustomerPersona.isPending
            }
          >
            Save
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className='space-y-6 mb-5 pr-4'>
          <div className='animate-pulse'>
            <div className='h-4 bg-gray-200 rounded w-1/4 mb-4'></div>
            <div className='space-y-4'>
              <div className='h-10 bg-gray-200 rounded'></div>
              <div className='h-10 bg-gray-200 rounded'></div>
              <div className='h-10 bg-gray-200 rounded'></div>
            </div>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className='space-y-6 mb-5 pr-4'>
          {fields.map((field, index) => {
            const persona = form.getValues().customer_personas[index];
            const personaKey = persona.id || `new_${index}`;
            const isExpanded = expandedPersonas.has(personaKey) || index === 0;

            return (
              <CustomerPersonaFormItem
                key={field.id}
                index={index}
                onDelete={handlePersonaDelete}
                fieldOptions={fieldOptions}
                form={form}
                isExpanded={isExpanded}
              />
            );
          })}
          {fields.length < 2 && (
            <Button
              variant='secondary'
              size='xsmall'
              onClick={handleAddPersona}
            >
              Add Another Persona
            </Button>
          )}
        </form>
      )}
    </div>
  );
};

interface CustomerPersonaFormItemProps {
  index: number;
  onDelete: (index: number) => void;
  fieldOptions?: Record<string, Array<{ key: string; display: string }>>;
  form: any;
  isExpanded: boolean;
}

const CustomerPersonaFormItem = ({
  index,
  onDelete,
  fieldOptions,
  form,
  isExpanded,
}: CustomerPersonaFormItemProps) => {
  const persona = form.getValues().customer_personas[index];

  return (
    <FormGroup
      title={
        !persona.id
          ? 'Adding New Persona'
          : form.getValues(`customer_personas.${index}.search_title`) ||
            `Persona ${index + 1}`
      }
      defaultCollapsed={!isExpanded}
    >
      <div className='space-y-4'>
        {!persona.id && (
          <div className='text-sm font-medium text-gray-600 mb-2 italic'>
            Adding New Persona
          </div>
        )}
        <FormField
          label='Persona Name'
          error={
            form.formState.errors.customer_personas?.[index]?.search_title
              ?.message
          }
        >
          <Controller
            name={`customer_personas.${index}.search_title`}
            control={form.control}
            render={({ field }) => (
              <Input
                aria-label='Persona Name'
                placeholder='Ex: Power User, Marketing Mike'
                {...field}
              />
            )}
          />
        </FormField>

        <FormField
          label='Title'
          required
          error={
            form.formState.errors.customer_personas?.[index]?.job_title?.message
          }
        >
          <Controller
            name={`customer_personas.${index}.job_title`}
            control={form.control}
            render={({ field }) => (
              <Input
                aria-label='Job Title'
                placeholder='What does this person do for a living'
                {...field}
              />
            )}
          />
        </FormField>

        <FormField
          label='Industry'
          error={
            form.formState.errors.customer_personas?.[index]?.industry?.message
          }
        >
          <Controller
            name={`customer_personas.${index}.industry`}
            control={form.control}
            render={({ field }) => (
              <Select
                value={field.value}
                onChange={(value) => {
                  field.onChange(value);
                }}
                options={(
                  fieldOptions?.['customer_personas.industry'] || []
                ).map((option) => ({
                  value: option.key,
                  label: option.display,
                }))}
                placeholder='Select industry'
              />
            )}
          />
        </FormField>

        <FormField
          label='Company Size'
          error={
            form.formState.errors.customer_personas?.[index]?.company_size
              ?.message
          }
        >
          <Controller
            name={`customer_personas.${index}.company_size`}
            control={form.control}
            render={({ field }) => (
              <Select
                value={field.value}
                onChange={(value) => {
                  field.onChange(value);
                }}
                options={(
                  fieldOptions?.['customer_personas.company_size'] || []
                ).map((option) => ({
                  value: option.key,
                  label: option.display,
                }))}
                placeholder='Select company size'
              />
            )}
          />
        </FormField>

        <FormField
          label='Age Range'
          error={
            form.formState.errors.customer_personas?.[index]?.age_range?.message
          }
        >
          <Controller
            name={`customer_personas.${index}.age_range`}
            control={form.control}
            render={({ field }) => (
              <Select
                value={field.value}
                onChange={(value) => {
                  field.onChange(value);
                }}
                options={(
                  fieldOptions?.['customer_personas.age_range'] || []
                ).map((option) => ({
                  value: option.key,
                  label: option.display,
                }))}
                placeholder='Select age range'
              />
            )}
          />
        </FormField>

        <FormField
          label='Geographical Location'
          error={
            form.formState.errors.customer_personas?.[index]?.country?.message
          }
        >
          <Controller
            name={`customer_personas.${index}.country`}
            control={form.control}
            render={({ field }) => (
              <Select
                value={field.value}
                onChange={(value) => {
                  field.onChange(value);
                }}
                options={(
                  fieldOptions?.['customer_personas.country'] || []
                ).map((option) => ({
                  value: option.key,
                  label: option.display,
                }))}
                placeholder='Select country'
              />
            )}
          />
        </FormField>

        <FormField
          label='Income Range'
          error={
            form.formState.errors.customer_personas?.[index]?.income_range
              ?.message
          }
        >
          <Controller
            name={`customer_personas.${index}.income_range`}
            control={form.control}
            render={({ field }) => (
              <Select
                value={field.value}
                onChange={(value) => {
                  field.onChange(value);
                }}
                options={(
                  fieldOptions?.['customer_personas.income_range'] || []
                ).map((option) => ({
                  value: option.key,
                  label: option.display,
                }))}
                placeholder='Select income range'
              />
            )}
          />
        </FormField>

        <FormField
          label='Education'
          error={
            form.formState.errors.customer_personas?.[index]?.education_level
              ?.message
          }
        >
          <Controller
            name={`customer_personas.${index}.education_level`}
            control={form.control}
            render={({ field }) => (
              <Select
                value={field.value}
                onChange={(value) => {
                  field.onChange(value);
                }}
                options={(
                  fieldOptions?.['customer_personas.education_level'] || []
                ).map((option) => ({
                  value: option.key,
                  label: option.display,
                }))}
                placeholder='Select education level'
              />
            )}
          />
        </FormField>

        <FormField
          label="What is your customer's buying behavior?"
          error={
            form.formState.errors.customer_personas?.[index]?.buying_behavior
              ?.message
          }
        >
          <Controller
            name={`customer_personas.${index}.buying_behavior`}
            control={form.control}
            render={({ field }) => (
              <Select
                value={field.value}
                onChange={(value) => {
                  field.onChange(value);
                }}
                options={(
                  fieldOptions?.['customer_personas.buying_behavior'] || []
                ).map((option) => ({
                  value: option.key,
                  label: option.display,
                }))}
                placeholder='Select buying behavior'
              />
            )}
          />
        </FormField>

        <FormField
          label='Customer Pain Points'
          error={
            form.formState.errors.customer_personas?.[index]
              ?.customer_pain_points?.message
          }
        >
          <Controller
            name={`customer_personas.${index}.customer_pain_points`}
            control={form.control}
            render={({ field }) => (
              <MultiValueTextArea
                value={field.value}
                onUpdate={(value) => {
                  field.onChange(value);
                }}
                placeholder='Enter one per line'
                description='Enter one pain point per line'
              />
            )}
          />
        </FormField>

        <FormField
          label='How does this user solve these pain points today?'
          error={
            form.formState.errors.customer_personas?.[index]
              ?.customer_pain_point_solutions?.message
          }
        >
          <Controller
            name={`customer_personas.${index}.customer_pain_point_solutions`}
            control={form.control}
            render={({ field }) => (
              <MultiValueTextArea
                value={field.value}
                onUpdate={(value) => {
                  field.onChange(value);
                }}
                placeholder='Enter one per line'
                description='Enter one solution per line'
              />
            )}
          />
        </FormField>

        <FormField
          label='What words do your customers use to describe their pains, needs, and desires?'
          error={
            form.formState.errors.customer_personas?.[index]
              ?.customer_vocabulary?.message
          }
        >
          <Controller
            name={`customer_personas.${index}.customer_vocabulary`}
            control={form.control}
            render={({ field }) => (
              <MultiValueTextArea
                value={field.value}
                onUpdate={(value) => {
                  field.onChange(value);
                }}
                placeholder='Enter one per line'
                description='Enter one concept per line'
              />
            )}
          />
        </FormField>

        <div className='flex justify-end'>
          <button
            type='button'
            onClick={() => onDelete(index)}
            className='text-sm underline text-[#345FF5] hover:text-red-700 hover:underline'
          >
            Delete Persona
          </button>
        </div>
      </div>
    </FormGroup>
  );
};
